import { useCallback, useEffect, useState } from "react";

import { SEARCH_DEBOUNCE_MS } from "../../../../../utils/constants";
import { DEFAULT_ACTIVITY_REQUEST } from "../../../activityRequest";
import { ActivityRequest } from "../../../types";
import { UseActivityRequestReturn } from "./types";

const FIRST_NUMBERED_PAGE: ActivityRequest = {
  ...DEFAULT_ACTIVITY_REQUEST,
  page: 1,
};

export const useActivityRequest = (
  accountId: number,
): UseActivityRequestReturn => {
  const [request, setRequest] = useState<ActivityRequest>(FIRST_NUMBERED_PAGE);
  const [searchInput, setSearchInput] = useState("");
  const [isCursorMode, setIsCursorMode] = useState(false);
  const [requestAccountId, setRequestAccountId] = useState(accountId);

  // Reset during render, not in an effect, so the new account's first read never carries the old one's filters.
  if (requestAccountId !== accountId) {
    setRequestAccountId(accountId);
    setRequest(FIRST_NUMBERED_PAGE);
    setSearchInput("");
    setIsCursorMode(false);
  }

  const firstPage = isCursorMode ? null : 1;

  const updateFilters = useCallback(
    (patch: Partial<Omit<ActivityRequest, "page">>) => {
      setRequest((prev) => ({ ...prev, ...patch, page: firstPage }));
    },
    [firstPage],
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setRequest((prev) =>
        prev.search === searchInput
          ? prev
          : { ...prev, search: searchInput, page: firstPage },
      );
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchInput, firstPage]);

  const setPage = useCallback((page: number) => {
    setRequest((prev) => ({ ...prev, page }));
  }, []);

  const switchToCursorMode = useCallback(() => {
    setIsCursorMode(true);
    setRequest((prev) => ({ ...prev, page: null }));
  }, []);

  const hasActiveFilters =
    request.search.trim() !== "" ||
    request.types.length > 0 ||
    request.holdingIds.length > 0 ||
    request.accountLevel;

  return {
    request,
    searchInput,
    setSearchInput,
    updateFilters,
    setPage,
    isCursorMode,
    switchToCursorMode,
    hasActiveFilters,
  };
};
